/* eslint-disable no-param-reassign */
/* eslint-disable import/no-extraneous-dependencies */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $getNodeByKey,
  $getRoot,
  $isElementNode,
  $isTextNode,
  type LexicalEditor,
  type NodeKey,
  type ElementFormatType,
  type RangeSelection,
  type LexicalNode,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isParentElementRTL,
  $wrapNodes,
  $patchStyleText,
  $isAtNodeEnd,
  $getSelectionStyleValueForProperty,
} from '@lexical/selection';
import {
  $getNearestNodeOfType,
  $findMatchingParent,
  mergeRegister,
  $getNearestBlockElementAncestorOrThrow,
} from '@lexical/utils';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import { createPortal } from 'react-dom';
import { $createQuoteNode, $isQuoteNode } from '@lexical/rich-text';
import {
  $createCodeNode,
  $isCodeNode,
  getDefaultCodeLanguage,
  getCodeLanguages,
} from '@lexical/code';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/extension';
import { INSERT_EMBED_COMMAND } from '@lexical/react/LexicalAutoEmbedPlugin';
import {
  Undo2,
  Redo2,
  Bold,
  Italic,
  Underline,
  Code,
  Link2,
  Type,
  Type as HeadingIcon,
  List,
  ListOrdered,
  Quote,
  FileCode,
  Plus,
  SeparatorHorizontal,
  FileMinus,
  Image as ImageIcon,
  Table as TableIcon,
  Columns as ColumnsIcon,
  StickyNote,
  ChevronRight,
  Youtube,
  Globe,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Outdent,
  Indent,
  ChevronDown,
  Pencil,
  Trash2,
} from 'lucide-react';
import DropdownColorPicker from '../ui/dropdown-color-picker';
import { INSERT_PAGE_BREAK } from './page-break-plugin';
import useModal from '../hooks/use-modal';
import { InsertTableDialog } from './table-plugin';
import { InsertImageDialog } from './images-plugin';
import { $createCustomHeadingNode, $isCustomHeadingNode } from '..';
import { InsertInlineImageDialog } from './inline-image-plugin';
import { InsertLayoutDialog } from './layout-plugin';
import { INSERT_COLLAPSIBLE_COMMAND } from './collapsible-plugin';
import { $createStickyNode } from '../nodes/sticky-node';
import { $isTableSelection } from '@lexical/table';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ToolbarButton, ToolbarDivider } from '../ui/toolbar-button';

const SUPPORTED_URL_PROTOCOLS = new Set([
  'http:',
  'https:',
  'mailto:',
  'sms:',
  'tel:',
]);

export function sanitizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    // eslint-disable-next-line no-script-url
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return 'about:blank';
    }
  } catch {
    return url;
  }
  return url;
}

// Source: https://stackoverflow.com/a/8234912/2013580
const urlRegExp = new RegExp(
  /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/
);

export function validateUrl(url: string): boolean {
  return url === 'https://' || urlRegExp.test(url);
}

const LowPriority = 1;

const supportedBlockTypes = new Set([
  'paragraph',
  'quote',
  'code',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
]);

const blockTypeToBlockName = {
  paragraph: 'Normal',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  ul: 'Bullet List',
  ol: 'Numbered List',
  quote: 'Quote',
  code: 'Code Block',
} as const;

function ElementFormatDropdown({
  editor,
  value,
}: {
  editor: LexicalEditor;
  value: string;
  isRTL: boolean;
}): React.JSX.Element {
  const ELEMENT_FORMAT_OPTIONS = [
    {
      id: 'left',
      icon: AlignLeft,
      name: 'Left Align',
      command: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left'),
    },
    {
      id: 'center',
      icon: AlignCenter,
      name: 'Center Align',
      command: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center'),
    },
    {
      id: 'right',
      icon: AlignRight,
      name: 'Right Align',
      command: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right'),
    },
    {
      id: 'justify',
      icon: AlignJustify,
      name: 'Justify Align',
      command: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify'),
    },
    {
      id: 'outdent',
      icon: Outdent,
      name: 'Outdent',
      command: () => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined),
    },
    {
      id: 'indent',
      icon: Indent,
      name: 'Indent',
      command: () => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined),
    },
  ];

  const currentOption =
    ELEMENT_FORMAT_OPTIONS.find((opt) => opt.id === value) ||
    ELEMENT_FORMAT_OPTIONS[0];
  const Icon = currentOption.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Icon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {ELEMENT_FORMAT_OPTIONS.map((opt) => (
          <DropdownMenuItem key={opt.id} onClick={opt.command}>
            <opt.icon className="mr-2 h-4 w-4" />
            <span>{opt.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function InsertElement({
  editor,
  chatRich,
  onUploadFile,
}: {
  editor: LexicalEditor;
  chatRich?: boolean;
  onUploadFile?: (file: File) => Promise<string>;
}): React.JSX.Element {
  const [modal, showModal] = useModal();

  const options = [
    {
      name: 'Horizontal Rule',
      icon: SeparatorHorizontal,
      action: () =>
        editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
    },
    {
      name: 'Page Break',
      icon: FileMinus,
      action: () => editor.dispatchCommand(INSERT_PAGE_BREAK, undefined),
    },
    {
      name: 'Image',
      icon: ImageIcon,
      action: () =>
        showModal('Insert Image', (onClose) => (
          <InsertImageDialog editor={editor} onClose={onClose} uploadFile={onUploadFile} />
        )),
    },
    {
      name: 'Inline Image',
      icon: ImageIcon,
      action: () =>
        showModal('Insert Inline Image', (onClose) => (
          <InsertInlineImageDialog editor={editor} onClose={onClose} uploadFile={onUploadFile} />
        )),
    },
    {
      name: 'Table',
      icon: TableIcon,
      action: () =>
        showModal('Insert Table', (onClose) => (
          <InsertTableDialog editor={editor} onClose={onClose} />
        )),
    },
    {
      name: 'Columns layout',
      icon: ColumnsIcon,
      action: () =>
        showModal('Insert Columns Layout', (onClose) => (
          <InsertLayoutDialog editor={editor} onClose={onClose} />
        )),
    },
    {
      name: 'Sticky note',
      icon: StickyNote,
      action: () =>
        editor.update(() => {
          const root = $getRoot();
          const rootElem = editor.getRootElement();
          let x = 20;
          let y = 20;
          if (rootElem) {
            const rect = rootElem.getBoundingClientRect();
            x = Math.max(20, Math.floor(rect.width / 2 - 100));
            y = 20;
          }
          root.append($createStickyNode(x, y));
        }),
    },

    {
      name: 'Collapsible',
      icon: ChevronRight,
      action: () => editor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, undefined),
    },
    {
      name: 'YouTube',
      icon: Youtube,
      action: () => editor.dispatchCommand(INSERT_EMBED_COMMAND, 'youtube-video'),
    },
    {
      name: 'Embed',
      icon: Globe,
      action: () => editor.dispatchCommand(INSERT_EMBED_COMMAND, 'figma'),
    },
  ];

  const chatOptions = [
    {
      name: 'Image',
      icon: ImageIcon,
      action: () =>
        showModal('Insert Image', (onClose) => (
          <InsertImageDialog editor={editor} onClose={onClose} uploadFile={onUploadFile} />
        )),
    },
  ];

  const currentOptions = chatRich ? chatOptions : options;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 font-medium">
            <Plus className="h-4 w-4" />
            <span>Insert</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {currentOptions.map((opt) => (
            <DropdownMenuItem key={opt.name} onClick={opt.action}>
              <opt.icon className="mr-2 h-4 w-4" />
              <span>{opt.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {modal}
    </>
  );
}

function positionEditorElement(editorEl: HTMLElement, rect: DOMRect | null): void {
  if (rect === null) {
    editorEl.style.opacity = '0';
    editorEl.style.top = '-1000px';
    editorEl.style.left = '-1000px';
  } else {
    editorEl.style.opacity = '1';
    editorEl.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
    editorEl.style.left = `${rect.left + window.pageXOffset - editorEl.offsetWidth / 2 + rect.width / 2}px`;
  }
}

function getSelectedNode(selection: RangeSelection): LexicalNode {
  const { focus } = selection;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  }
  return $isAtNodeEnd(selection.anchor) ? focusNode : anchorNode;
}

function FloatingLinkEditor({ editor }: { editor: LexicalEditor }): React.JSX.Element {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mouseDownRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [isEditMode, setEditMode] = useState(false);
  const [lastSelection, setLastSelection] = useState<RangeSelection | null>(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
    }

    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const { activeElement } = document;

    if (editorElem === null) return;

    const rootElement = editor.getRootElement();
    if (
      $isRangeSelection(selection) &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect: DOMRect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild as HTMLElement;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (
      !activeElement ||
      (activeElement.className !== 'text-blue-500 mr-5 block' &&
        activeElement.tagName !== 'INPUT')
    ) {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [editor]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(() => {
          if (editor.getRootElement() !== null) {
            editor.read(() => {
              updateLinkEditor();
            });
          }
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            editor.read(() => {
              updateLinkEditor();
            });
            return false;
          },
          LowPriority
        )
      ),
    [editor, updateLinkEditor]
  );

  // ✅ was editor.getEditorState().read(...) — change to editor.read(...)
  useEffect(() => {
    editor.read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  return (
    <div
      ref={editorRef}
      className="link-editor"
    >
      {isEditMode ? (
        <input
          ref={inputRef}
          className="block relative outline-none px-3 py-1 text-sm text-blue-500 bg-white w-64"
          value={linkUrl}
          onChange={(event) => setLinkUrl(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              if (lastSelection !== null) {
                if (linkUrl !== '') {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
                    url: sanitizeUrl(linkUrl),
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  } as any);
                }
                setEditMode(false);
              }
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setEditMode(false);
            }
          }}
        />
      ) : (
        <div className="flex px-3 py-1 items-center gap-2 justify-between bg-white rounded">
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm underline max-w-[200px] truncate"
          >
            {linkUrl}
          </a>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setEditMode(true)}
          >
            <Pencil className="h-3 w-3 text-gray-500" />
          </Button>
        </div>
      )}
    </div>
  );
}

function BlockOptionsDropdownList({
  editor,
  blockType,
}: {
  editor: LexicalEditor;
  blockType: string;
  chatRich?: boolean;
}): React.JSX.Element {
  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
  };

  const formatHeading = (level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => {
    if (blockType !== level) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createCustomHeadingNode(level));
        }
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createCodeNode());
        }
      });
    }
  };

  const options = [
    { id: 'paragraph', name: 'Normal', icon: Type, action: formatParagraph },
    { id: 'h1', name: 'Heading 1', icon: HeadingIcon, action: () => formatHeading('h1') },
    { id: 'h2', name: 'Heading 2', icon: HeadingIcon, action: () => formatHeading('h2') },
    { id: 'h3', name: 'Heading 3', icon: HeadingIcon, action: () => formatHeading('h3') },
    { id: 'ul', name: 'Bullet List', icon: List, action: formatBulletList },
    { id: 'ol', name: 'Numbered List', icon: ListOrdered, action: formatNumberedList },
    { id: 'quote', name: 'Quote', icon: Quote, action: formatQuote },
    { id: 'code', name: 'Code Block', icon: FileCode, action: formatCode },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs font-medium">
          <span>
            {blockTypeToBlockName[blockType as keyof typeof blockTypeToBlockName] || 'Normal'}
          </span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((opt) => (
          <DropdownMenuItem key={opt.id} onClick={opt.action}>
            <opt.icon className="mr-2 h-4 w-4" />
            <span>{opt.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FormattingOptions({
  editor,
  clearFormatting,
}: {
  editor: LexicalEditor;
  clearFormatting: () => void;
  chatRich?: boolean;
}): React.JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Type className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}>
          <span>Strikethrough</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')}>
          <span>Subscript</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')}>
          <span>Superscript</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={clearFormatting}>
          <Trash2 className="mr-2 h-4 w-4" />
          <span>Clear formatting</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ToolbarPlugin({
  toolbarstyle,
  chatRich,
  onUploadFile,
}: {
  toolbarstyle?: string;
  chatRich?: boolean;
  onUploadFile?: (file: File) => Promise<string>;
}): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(null);
  const [codeLanguage, setCodeLanguage] = useState('');
  const [isRTL, setIsRTL] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');
  const [fontColor, setFontColor] = useState<string>('#000');
  const [bgColor, setBgColor] = useState<string>('#fff');

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);

        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const tag = $isCustomHeadingNode(element)
            ? element.getTag()
            : (element as any).getTag?.() || element.getType();
          setBlockType(tag);

          if ($isCodeNode(element)) {
            setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage());
          }
        }
      }

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsCode(selection.hasFormat('code'));

      // ✅ needs active editor => this whole function must be called from editor.read()
      setIsRTL($isParentElementRTL(selection));

      const node = getSelectedNode(selection);
      const parent = node.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(node));

      let matchingParent: LexicalNode | null = null;
      if ($isLinkNode(parent)) {
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
        );
      }

      setElementFormat(
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : (parent as any)?.getFormatType?.() || 'left'
      );

      setFontColor(
        $getSelectionStyleValueForProperty(selection, 'color', '#000')
      );
      setBgColor(
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#fff'
        )
      );
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      // ✅ was editorState.read(...) — change to editor.read(...)
      editor.registerUpdateListener(() => {
        if (editor.getRootElement() !== null) {
          editor.read(() => {
            updateToolbar();
          });
        }
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          editor.read(() => {
            updateToolbar();
          });
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload: boolean) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload: boolean) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: 'historic' } : {}
      );
    },
    [editor]
  );

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack?: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack?: boolean) => {
      applyStyleText({ 'background-color': value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const clearFormatting = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach((node) => {
          if ($isTextNode(node)) {
            node.setStyle('');
            node.setFormat(0);
            $getNearestBlockElementAncestorOrThrow(node).setFormat(0 as any);
          } else if ($isCustomHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat(0 as any);
          }
        });
      }
    });
  }, [editor]);

  return (
    <div className={`toolbar ${toolbarstyle || 'rounded-t-lg top-16 sticky z-10'}`}>
      {!chatRich && (
        <>
          <ToolbarButton
            onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
            disabled={!canUndo}
            tooltip="Undo (Ctrl+Z)"
          >
            <Undo2 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
            disabled={!canRedo}
            tooltip="Redo (Ctrl+Y)"
          >
            <Redo2 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarDivider />
        </>
      )}

      {supportedBlockTypes.has(blockType) && (
        <>
          <BlockOptionsDropdownList editor={editor} blockType={blockType} />
          <ToolbarDivider />
        </>
      )}

      {blockType === 'code' ? (
        <select
          className="flex h-8 rounded border border-border bg-card px-2 text-xs font-medium focus:ring-0 capitalize cursor-pointer text-foreground"
          onChange={(e) =>
            editor.update(() => {
              if (selectedElementKey) {
                const node = $getNodeByKey(selectedElementKey);
                if ($isCodeNode(node)) node.setLanguage(e.target.value);
              }
            })
          }
          value={codeLanguage}
        >
          {getCodeLanguages().map((lang) => (
            <option key={lang} value={lang} className="bg-card text-foreground">
              {lang}
            </option>
          ))}
        </select>
      ) : (
        <>
          <div className="flex items-center gap-0.5">
            <ToolbarButton
              onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
              active={isBold}
              tooltip="Bold (Ctrl+B)"
            >
              <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
              active={isItalic}
              tooltip="Italic (Ctrl+I)"
            >
              <Italic className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
              active={isUnderline}
              tooltip="Underline (Ctrl+U)"
            >
              <Underline className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
              active={isCode}
              tooltip="Inline Code"
            >
              <Code className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() =>
                !isLink
                  ? editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
                    url: sanitizeUrl(''),
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  } as any)
                  : editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
              }
              active={isLink}
              tooltip="Link"
            >
              <Link2 className="h-4 w-4" />
            </ToolbarButton>
          </div>

          {isLink && createPortal(<FloatingLinkEditor editor={editor} />, document.body)}

          <ToolbarDivider />

          <DropdownColorPicker
            color={fontColor}
            onChange={onFontColorSelect}
            title="Text color"
            buttonIcon="Type"
          />
          <DropdownColorPicker
            color={bgColor}
            onChange={onBgColorSelect}
            title="Background color"
            buttonIcon="PaintBucket"
          />

          <FormattingOptions editor={editor} clearFormatting={clearFormatting} />

          <ToolbarDivider />

          <InsertElement editor={editor} chatRich={chatRich} onUploadFile={onUploadFile} />

          {!chatRich && (
            <>
              <ToolbarDivider />
              <ElementFormatDropdown editor={editor} isRTL={isRTL} value={elementFormat} />
            </>
          )}
        </>
      )}
    </div>
  );
}
